import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Pestañas por modalidad → familia real de lottery_types
export const MODALIDADES = [
  { key: "ANIMALITOS_CLS", label: "ANIMALITOS CLS", family: "animalitos" },
  { key: "ANIMALITOS_ESP", label: "ANIMALITOS ESP", family: "animalitos" },
  { key: "TERMINALES", label: "TERMINALES", family: "chance" },
  { key: "TRIPLES", label: "TRIPLES", family: "tradicional" },
  { key: "TRIPLETA", label: "TRIPLETA", family: "tradicional" },
];

const DEFAULT_MONTO = 1500.0;
const DEFAULT_LIMITE_TRIPLETA = 30;
const CURRENCY_KEYS = ["BS", "USD", "COP", "VES"];
const CURRENCY_LABELS = { BS: "Bolívares (Bs)", USD: "Dólares (USD)", COP: "Pesos (COP)" };
const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));

const emptyGeneral = () => ({ enabled: false, cupo: DEFAULT_MONTO, term_pct: 0, tript_pct: 0 });
const emptyRow = () => ({ edit: false, monto: DEFAULT_MONTO, terminal: 0, triple: 0 });
const emptyHeader = () => ({ participacion: 0, utilidad: 0, limite_tripleta: DEFAULT_LIMITE_TRIPLETA, limite_ventas: 0 });

const PARENT_LIST_KEYS = { banca: ["bancas"], grupo: ["grupos"], agencia: ["agencias"], taquilla: ["taquillas"] };

export default function EntityConfigDialog({ open, onClose, entityType, entity }) {
  const qc = useQueryClient();
  // Divisas habilitadas de la entidad (VES normalizado a BS)
  const entityCurrencies = useMemo(() => {
    const raw = (entity?.currencies?.length ? entity.currencies : [entity?.currency || "BS"]) || ["BS"];
    const seen = [];
    for (const c of raw) {
      const u = String(c).toUpperCase() === "VES" ? "BS" : String(c).toUpperCase();
      if (!seen.includes(u)) seen.push(u);
    }
    return seen.length ? seen : ["BS"];
  }, [entity]);

  const [currency, setCurrency] = useState(entityCurrencies[0] || "BS");
  // allCfg: { BS: {header, modalidades}, USD: {...} }
  const [allCfg, setAllCfg] = useState({});
  const [activeTab, setActiveTab] = useState(MODALIDADES[0].key);

  const { data: lotteriesData } = useQuery({
    queryKey: ["lottery-types", "all"],
    queryFn: async () => (await api.get("/lottery-types")).data,
    enabled: open,
  });
  const lotteries = useMemo(() => lotteriesData || [], [lotteriesData]);

  const cfgKey = ["entity-config", entityType, entity?.id];
  // Siempre lee el snapshot persistido al abrir (ignora staleTime global de 30s)
  const { data: cfgData, isFetching: cfgFetching } = useQuery({
    queryKey: cfgKey,
    queryFn: async () => (await api.get(`/entity-config/${entityType}/${entity.id}`)).data,
    enabled: open && !!entity?.id,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const lotteriesByMod = useMemo(() => {
    const map = {};
    for (const m of MODALIDADES) {
      const list = lotteries.filter((l) => l.family === m.family);
      map[m.key] = list.length ? list : [{ id: "__base__", name: "Configuración base", code: "BASE" }];
    }
    return map;
  }, [lotteries]);

  // Construye el corte (slice) por divisa a partir de la config guardada
  const buildSlice = (slice) => {
    const header = {
      participacion: num(slice?.participacion),
      utilidad: num(slice?.utilidad),
      limite_tripleta: slice?.limite_tripleta != null ? num(slice.limite_tripleta) : DEFAULT_LIMITE_TRIPLETA,
      limite_ventas: num(slice?.limite_ventas),
    };
    const mods = {};
    for (const m of MODALIDADES) {
      const saved = (slice?.modalidades || {})[m.key] || {};
      const rows = {};
      for (const lot of lotteriesByMod[m.key] || []) {
        const sr = (saved.loterias || {})[lot.id];
        rows[lot.id] = sr ? { edit: !!sr.edit, monto: num(sr.monto), terminal: num(sr.terminal), triple: num(sr.triple) } : emptyRow();
      }
      mods[m.key] = { general: { ...emptyGeneral(), ...(saved.general || {}) }, loterias: rows };
    }
    return { header, modalidades: mods };
  };

  // Slice persistido en backend para una divisa
  const persistedSlice = (c) => {
    const cfg = cfgData?.config || {};
    const hasPerCurrency = Object.keys(cfg).some((k) => CURRENCY_KEYS.includes(k.toUpperCase()) && typeof cfg[k] === "object");
    if (hasPerCurrency) return buildSlice(cfg[c] || cfg[c === "BS" ? "VES" : c] || {});
    return buildSlice(c === entityCurrencies[0] ? cfg : {}); // formato plano heredado → primera divisa
  };

  useEffect(() => {
    if (!open || cfgFetching) return;
    const next = {};
    for (const c of entityCurrencies) next[c] = persistedSlice(c);
    setAllCfg(next);
    setCurrency(entityCurrencies[0] || "BS");
    setActiveTab(MODALIDADES[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cfgData, cfgFetching, lotteriesByMod, entityCurrencies]);

  // Al cambiar de divisa se descartan ediciones no guardadas de la divisa saliente
  const switchCurrency = (c) => {
    if (c === currency) return;
    setAllCfg((prev) => ({ ...prev, [currency]: persistedSlice(currency) }));
    setCurrency(c);
  };

  const cur = allCfg[currency] || { header: emptyHeader(), modalidades: {} };
  const header = cur.header || emptyHeader();
  const modalidades = cur.modalidades || {};

  const setHeader = (patch) => setAllCfg((prev) => ({ ...prev, [currency]: { ...prev[currency], header: { ...prev[currency].header, ...patch } } }));
  const setRow = (mod, lotId, patch) => setAllCfg((prev) => ({
    ...prev, [currency]: { ...prev[currency], modalidades: { ...prev[currency].modalidades, [mod]: { ...prev[currency].modalidades[mod], loterias: { ...prev[currency].modalidades[mod].loterias, [lotId]: { ...prev[currency].modalidades[mod].loterias[lotId], ...patch } } } } },
  }));
  const setGeneral = (mod, patch) => setAllCfg((prev) => ({
    ...prev, [currency]: { ...prev[currency], modalidades: { ...prev[currency].modalidades, [mod]: { ...prev[currency].modalidades[mod], general: { ...prev[currency].modalidades[mod].general, ...patch } } } },
  }));

  const applyGeneral = (mod) => {
    const g = modalidades[mod].general;
    setAllCfg((prev) => {
      const rows = { ...prev[currency].modalidades[mod].loterias };
      for (const id of Object.keys(rows)) rows[id] = { ...rows[id], monto: num(g.cupo), terminal: num(g.term_pct), triple: num(g.tript_pct) };
      return { ...prev, [currency]: { ...prev[currency], modalidades: { ...prev[currency].modalidades, [mod]: { ...prev[currency].modalidades[mod], loterias: rows } } } };
    });
    toast.success("Configuración general aplicada a la tabla");
  };

  const loadTemplate = () => {
    setAllCfg((prev) => {
      const nextMods = {};
      for (const m of MODALIDADES) {
        const rows = {};
        for (const lot of lotteriesByMod[m.key] || []) rows[lot.id] = emptyRow();
        nextMods[m.key] = { general: emptyGeneral(), loterias: rows };
      }
      return { ...prev, [currency]: { header: { ...emptyHeader() }, modalidades: nextMods } };
    });
    toast.success(`Plantilla por defecto cargada (${currency})`);
  };

  const save = useMutation({
    mutationFn: async () => {
      const config = {};
      for (const c of entityCurrencies) {
        const slice = allCfg[c] || { header: emptyHeader(), modalidades: {} };
        config[c] = { ...slice.header, modalidades: slice.modalidades };
      }
      return (await api.put(`/entity-config/${entityType}/${entity.id}`, { config })).data;
    },
    onSuccess: async () => {
      toast.success("Configuración guardada");
      qc.removeQueries({ queryKey: cfgKey, exact: true });
      await qc.invalidateQueries({ queryKey: ["entity-config"] });
      qc.invalidateQueries({ queryKey: PARENT_LIST_KEYS[entityType] || [] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar configuración"),
  });

  const activeMod = modalidades[activeTab] || { general: emptyGeneral(), loterias: {} };
  const activeRows = lotteriesByMod[activeTab] || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="entity-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>Configuración de cupos y comisiones</span>
            <span className="text-xs font-normal text-muted-foreground">{entity?.name} · <span className="uppercase">{entityType}</span></span>
          </DialogTitle>
        </DialogHeader>

        {/* Selector de divisa (multidivisa, solo las habilitadas en la entidad) */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="config-currency-selector">
          <Label className="text-xs mr-1">Divisa</Label>
          {entityCurrencies.map((c) => (
            <button key={c} type="button" onClick={() => switchCurrency(c)} data-testid={`config-currency-${c}`}
              className={`rounded-full border-2 px-3.5 py-1.5 text-xs font-bold transition-colors ${currency === c ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
              {CURRENCY_LABELS[c] || c}
            </button>
          ))}
          <span className="text-[11px] text-muted-foreground ml-1">Cada divisa guarda su propia configuración.</span>
        </div>

        {/* Cabecera del corte por divisa */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg border border-border bg-secondary/30 p-3">
          <div className="space-y-1"><Label className="text-xs">Participación %</Label><Input type="number" step="0.1" value={header.participacion} onChange={(e) => setHeader({ participacion: e.target.value })} data-testid="config-participacion"/></div>
          <div className="space-y-1"><Label className="text-xs">Utilidad %</Label><Input type="number" step="0.1" value={header.utilidad} onChange={(e) => setHeader({ utilidad: e.target.value })} data-testid="config-utilidad"/></div>
          <div className="space-y-1"><Label className="text-xs">Límite Tripleta</Label><Input type="number" step="1" value={header.limite_tripleta} onChange={(e) => setHeader({ limite_tripleta: e.target.value })} data-testid="config-limite-tripleta"/></div>
          <div className="space-y-1"><Label className="text-xs">Límite ventas <span className="text-[10px] text-muted-foreground">(0 = ilimitado)</span></Label><Input type="number" step="1" value={header.limite_ventas} onChange={(e) => setHeader({ limite_ventas: e.target.value })} data-testid="config-limite-ventas"/></div>
        </div>

        {/* Pestañas por modalidad */}
        <div className="flex flex-wrap gap-1.5" data-testid="config-modalidad-tabs">
          {MODALIDADES.map((m) => (
            <button key={m.key} onClick={() => setActiveTab(m.key)} data-testid={`config-tab-${m.key}`}
              className={`rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-colors ${activeTab === m.key ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Barra: aplicar configuración general */}
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-3" data-testid="config-general-bar">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 pb-1">
              <Switch checked={!!activeMod.general.enabled} onCheckedChange={(v) => setGeneral(activeTab, { enabled: v })} id="cfg-gen" data-testid="config-general-toggle"/>
              <Label htmlFor="cfg-gen" className="text-xs font-semibold">Aplicar configuración general</Label>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Cupo</Label><Input type="number" step="1" disabled={!activeMod.general.enabled} value={activeMod.general.cupo} onChange={(e) => setGeneral(activeTab, { cupo: e.target.value })} className="w-28" data-testid="config-general-cupo"/></div>
            <div className="space-y-1"><Label className="text-[11px]">% Term</Label><Input type="number" step="0.1" disabled={!activeMod.general.enabled} value={activeMod.general.term_pct} onChange={(e) => setGeneral(activeTab, { term_pct: e.target.value })} className="w-24" data-testid="config-general-term"/></div>
            <div className="space-y-1"><Label className="text-[11px]">% Tript</Label><Input type="number" step="0.1" disabled={!activeMod.general.enabled} value={activeMod.general.tript_pct} onChange={(e) => setGeneral(activeTab, { tript_pct: e.target.value })} className="w-24" data-testid="config-general-tript"/></div>
            <Button size="sm" variant="outline" disabled={!activeMod.general.enabled} onClick={() => applyGeneral(activeTab)} data-testid="config-general-apply">Aplicar a la tabla</Button>
          </div>
        </div>

        {/* Tabla de loterías */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="config-lotteries-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left bg-secondary/40">
                <th className="px-3 py-2">Lotería</th>
                <th className="px-3 py-2 text-center">¿Editar?</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2 text-right">Terminal</th>
                <th className="px-3 py-2 text-right">Triple</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.map((lot) => {
                const row = activeMod.loterias[lot.id] || emptyRow();
                return (
                  <tr key={lot.id} className="border-t border-border/60" data-testid={`config-row-${lot.code || lot.id}`}>
                    <td className="px-3 py-2 font-medium">{lot.name}{lot.code ? <span className="ml-1 font-mono text-xs text-muted-foreground">({lot.code})</span> : null}</td>
                    <td className="px-3 py-2 text-center"><Switch checked={!!row.edit} onCheckedChange={(v) => setRow(activeTab, lot.id, { edit: v })} data-testid={`config-row-edit-${lot.code || lot.id}`}/></td>
                    <td className="px-3 py-2 text-right"><Input type="number" step="0.01" disabled={!row.edit} value={row.monto} onChange={(e) => setRow(activeTab, lot.id, { monto: e.target.value })} className="w-28 ml-auto text-right" data-testid={`config-row-monto-${lot.code || lot.id}`}/></td>
                    <td className="px-3 py-2 text-right"><Input type="number" step="0.1" disabled={!row.edit} value={row.terminal} onChange={(e) => setRow(activeTab, lot.id, { terminal: e.target.value })} className="w-24 ml-auto text-right" data-testid={`config-row-terminal-${lot.code || lot.id}`}/></td>
                    <td className="px-3 py-2 text-right"><Input type="number" step="0.1" disabled={!row.edit} value={row.triple} onChange={(e) => setRow(activeTab, lot.id, { triple: e.target.value })} className="w-24 ml-auto text-right" data-testid={`config-row-triple-${lot.code || lot.id}`}/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={loadTemplate} data-testid="config-template-button"><FileDown size={14} className="mr-1.5"/>Plantilla ({currency})</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="config-cancel-button">Cancelar</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="config-accept-button">Aceptar</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
